import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditLogFeed = ({ data, loading }) => {
  const [expandedLog, setExpandedLog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const getRiskBadge = (level) => {
    const configs = {
      low: { color: 'bg-success/10 text-success border-success/20', icon: 'CheckCircle', label: 'Low Risk' },
      medium: { color: 'bg-warning/10 text-warning border-warning/20', icon: 'AlertCircle', label: 'Medium Risk' },
      high: { color: 'bg-error/10 text-error border-error/20', icon: 'AlertTriangle', label: 'High Risk' }
    };
    const config = configs?.[level] || configs?.low;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        {config?.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const configs = {
      success: { color: 'bg-success/10 text-success border-success/20', label: 'Success' },
      failed: { color: 'bg-error/10 text-error border-error/20', label: 'Failed' },
      pending: { color: 'bg-warning/10 text-warning border-warning/20', label: 'Pending' }
    };
    const config = configs?.[status] || configs?.success;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Authentication': 'Lock',
      'Data Access': 'Database',
      'System Configuration': 'Settings',
      'Export Activities': 'Download',
      'User Management': 'Users'
    };
    return icons?.[category] || 'Activity';
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const toggleExpand = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const paginatedData = data?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(data?.length / itemsPerPage);

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-4 md:p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-muted rounded w-1/3 mb-6"></div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5]?.map((i) => (
            <div key={i} className="h-24 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl shadow-sm">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg md:text-xl font-semibold text-foreground">Activity Feed</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" iconName="RefreshCw">
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="divide-y divide-border">
        {paginatedData?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No audit logs found</p>
          </div>
        ) : (
          paginatedData?.map((log) => (
            <div key={log?.id} className="p-4 md:p-6 hover:bg-muted/30 transition-colors">
              <div className="flex items-start gap-4">
                <div className={`p-2 rounded-lg ${
                  log?.riskLevel === 'high' ? 'bg-error/10' :
                  log?.riskLevel === 'medium'? 'bg-warning/10' : 'bg-success/10'
                }`}>
                  <Icon
                    name={getCategoryIcon(log?.actionCategory)}
                    size={20}
                    className={`${
                      log?.riskLevel === 'high' ? 'text-error' :
                      log?.riskLevel === 'medium'? 'text-warning' : 'text-success'
                    }`}
                  />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h4 className="font-semibold text-foreground">{log?.action}</h4>
                        {getRiskBadge(log?.riskLevel)}
                        {getStatusBadge(log?.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">{log?.description}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName={expandedLog === log?.id ? 'ChevronUp' : 'ChevronDown'}
                      onClick={() => toggleExpand(log?.id)}
                    >
                      Details
                    </Button>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                    <div className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      <span>{log?.userName} ({log?.userRole})</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Clock" size={12} />
                      <span>{formatTimestamp(log?.timestamp)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Icon name="Globe" size={12} />
                      <span>{log?.ipAddress}</span>
                    </div>
                  </div>

                  {expandedLog === log?.id && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">User ID</p>
                          <p className="text-sm text-foreground font-mono">{log?.userId}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Session ID</p>
                          <p className="text-sm text-foreground font-mono">{log?.sessionId}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Action Category</p>
                          <p className="text-sm text-foreground">{log?.actionCategory}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Affected Resource</p>
                          <p className="text-sm text-foreground">{log?.affectedResource}</p>
                        </div>
                      </div>

                      {log?.beforeState && log?.afterState && (
                        <div className="pt-3 border-t border-border">
                          <p className="text-xs font-medium text-muted-foreground mb-2">Configuration Changes</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Before</p>
                              <pre className="text-xs bg-background p-2 rounded border border-border overflow-x-auto">
                                {JSON.stringify(log?.beforeState, null, 2)}
                              </pre>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">After</p>
                              <pre className="text-xs bg-background p-2 rounded border border-border overflow-x-auto">
                                {JSON.stringify(log?.afterState, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="p-4 border-t border-border flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, data?.length)} of {data?.length} logs
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronLeft"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => prev - 1)}
            />
            <span className="text-sm text-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconName="ChevronRight"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => prev + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogFeed;